import { forwardRef, useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Check, CircleHelp, ExternalLink, Loader2, Sparkles, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StageBar, STAGE_META } from './StageBar';
import type { StepKey } from './types';

/** Expo-out, shared with aimdoc.ai: fast settle, no overshoot. */
export const ONBOARDING_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/**
 * Direction comes from a monotonic position the parent computes (step index,
 * with sub-steps folded in). Comparing against the previous render's position
 * means navigation callers never have to thread "forward vs back" through.
 */
function useDirection(position: number) {
  const previous = useRef(position);
  const direction = position > previous.current ? 1 : position < previous.current ? -1 : 0;
  useEffect(() => {
    previous.current = position;
  }, [position]);
  return direction;
}

const SHIFT = 28;

const sectionVariants = {
  enter: (direction: number) => ({ opacity: 0, x: direction === 0 ? 0 : direction > 0 ? SHIFT : -SHIFT }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: ONBOARDING_EASE, staggerChildren: 0.06, delayChildren: 0.02 },
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction === 0 ? 0 : direction > 0 ? -SHIFT : SHIFT,
    filter: 'blur(4px)',
    transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
  }),
};

/** Blur-up reveal for each block inside a step, staggered by the section. */
export const revealVariants = {
  enter: { opacity: 0, y: 14, filter: 'blur(6px)' },
  center: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.55, ease: ONBOARDING_EASE } },
  exit: { opacity: 0 },
};

/** Paper texture behind the stage: a dot field fading out from the top, under a soft brand glow. */
function StageBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0" aria-hidden>
      <div className="absolute inset-0 [background-image:radial-gradient(hsl(var(--foreground)/0.075)_1px,transparent_1px)] [background-size:20px_20px] [mask-image:radial-gradient(ellipse_75%_55%_at_50%_0%,black_20%,transparent_100%)]" />
      <div className="absolute left-1/2 top-[-18rem] h-[34rem] w-[64rem] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(17,115,240,0.14),transparent)] blur-2xl dark:bg-[radial-gradient(closest-side,rgba(59,139,255,0.16),transparent)]" />
    </div>
  );
}

/**
 * The onboarding frame. The whole stage is locked to the viewport
 * (h-[100dvh], overflow-hidden): the stage bar on top and the action bar at
 * the bottom never move, the content column sits dead-center between them,
 * and each step is designed against a content budget so it fits. The content
 * region is the only element that can scroll, and only as an escape hatch
 * (extreme zoom or very short viewports).
 *
 * `fill` gives the step the content region's full height instead of
 * centering it — the Building step uses this so its tracks flex to the screen.
 */
export function OnboardingStage({
  steps,
  current,
  position,
  transitionKey,
  title,
  subtitle,
  fill = false,
  wide = false,
  medium = false,
  onBack,
  backDisabled,
  action,
  secondaryAction,
  actionKey,
  footerNote,
  children,
}: {
  steps: StepKey[];
  current: StepKey;
  /** Monotonic navigation position; sign of the delta sets transition direction. */
  position: number;
  /** AnimatePresence key: step key, plus the sub-step when a step has several screens. */
  transitionKey: string;
  title: string;
  subtitle: string;
  fill?: boolean;
  /** Widen the content column (max-w-4xl) — the Building step's side-by-side tracks need the room. */
  wide?: boolean;
  /** max-w-3xl: two side-by-side cards (intent, payments) without cramping. */
  medium?: boolean;
  onBack?: () => void;
  backDisabled?: boolean;
  action?: ReactNode;
  /** Low-emphasis action shown in the dock before the primary one (e.g. "Continue without one"). */
  secondaryAction?: ReactNode;
  /** Crossfades the action bar button when its identity changes (skip → done). */
  actionKey?: string;
  footerNote?: ReactNode;
  children: ReactNode;
}) {
  const direction = useDirection(position);
  const index = Math.max(0, steps.indexOf(current));

  return (
    <MotionConfig reducedMotion="user">
      <div className="fixed inset-0 z-50 flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground">
        <StageBackdrop />

        <StageBar steps={steps} current={current} />

        <main className="relative z-10 min-h-0 flex-1">
          <AnimatePresence mode="popLayout" custom={direction} initial={false}>
            <motion.section
              key={transitionKey}
              custom={direction}
              variants={sectionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0 flex flex-col overflow-y-auto"
            >
              <div
                className={cn(
                  'flex w-full flex-col px-5 sm:px-8',
                  fill ? 'mx-auto h-full py-6' : 'm-auto py-8',
                  wide ? 'max-w-4xl' : medium ? 'max-w-3xl' : 'max-w-xl'
                )}
              >
                <motion.div variants={revealVariants} className="mb-7 shrink-0 text-center">
                  <div className="mb-3 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#1173F0] dark:text-[#3B8BFF]">
                    {String(index + 1).padStart(2, '0')} · {STAGE_META[current].label}
                  </div>
                  <h1 className="text-balance text-[26px] font-semibold leading-[1.15] tracking-[-0.025em] sm:text-[32px]">
                    {title}
                  </h1>
                  <p className="mx-auto mt-2.5 max-w-[520px] text-balance text-sm leading-6 text-muted-foreground sm:text-[15px]">
                    {subtitle}
                  </p>
                </motion.div>
                <motion.div variants={revealVariants} className={cn('flex flex-col', fill && 'min-h-0 flex-1')}>
                  {children}
                </motion.div>
              </div>
            </motion.section>
          </AnimatePresence>
        </main>

        {/* The action dock: a centered floating pill holding only the actions
            this step has (Back, an optional secondary, the primary). It
            resizes with a layout animation as actions come and go, so a lone
            Continue on the first step sits centered instead of off to one side. */}
        <footer
          className="relative z-10 flex shrink-0 flex-col items-center px-5 pt-2"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <motion.div
            layout
            transition={{ type: 'spring', stiffness: 420, damping: 36 }}
            className="flex max-w-full items-center gap-1 rounded-full border border-border/70 bg-card/85 p-1 shadow-[0_1px_2px_rgba(16,24,40,0.06),0_16px_40px_-16px_rgba(16,24,40,0.35)] backdrop-blur-xl dark:shadow-[0_16px_40px_-16px_rgba(0,0,0,0.8)]"
          >
            <AnimatePresence initial={false} mode="popLayout">
              {onBack && (
                <motion.button
                  key="back"
                  layout
                  type="button"
                  onClick={onBack}
                  disabled={backDisabled}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.18 }}
                  className="group inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full pl-3 pr-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" /> Back
                </motion.button>
              )}
              {secondaryAction && (
                <motion.div
                  key="secondary"
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.18 }}
                  className="flex shrink-0"
                >
                  {secondaryAction}
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div layout className="flex shrink-0">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={actionKey ?? 'action'}
                  initial={{ opacity: 0, y: 6, filter: 'blur(2px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -6, filter: 'blur(2px)' }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  {action}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
          {footerNote && (
            <div className="mt-2 max-w-xl text-balance text-center text-[11px] leading-4 text-muted-foreground">{footerNote}</div>
          )}
        </footer>
      </div>
    </MotionConfig>
  );
}

type ActionIcon = 'arrow' | 'ext' | 'spark';
const ACTION_ICONS: Record<ActionIcon, LucideIcon> = { arrow: ArrowRight, ext: ExternalLink, spark: Sparkles };

/**
 * The action bar's buttons. `primary` is the near-black pill with a brand
 * glow (aimdoc.ai's CTA); `quiet` is the low-emphasis pill for skip actions.
 */
export const StageButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; icon?: ActionIcon | null; tone?: 'primary' | 'quiet' }
>(function StageButton({ busy = false, icon = 'arrow', tone = 'primary', disabled, className, children, ...props }, ref) {
  const Icon = icon ? ACTION_ICONS[icon] : null;
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || busy}
      className={cn(
        'group relative inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-sm font-medium outline-none transition-[background-color,box-shadow,transform,opacity] duration-200 focus-visible:ring-2 focus-visible:ring-[#1173F0]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98] disabled:pointer-events-none',
        tone === 'primary'
          ? 'bg-foreground text-background shadow-[0_0_0_1px_hsl(var(--foreground)/0.1),0_8px_24px_-10px_rgba(17,115,240,0.65)] hover:shadow-[0_0_0_1px_hsl(var(--foreground)/0.1),0_12px_32px_-10px_rgba(17,115,240,0.9)] disabled:opacity-40 disabled:shadow-none'
          : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground disabled:opacity-50',
        className
      )}
      {...props}
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
      {!busy && Icon && (
        <Icon className={cn('h-4 w-4 transition-transform duration-200', icon === 'arrow' && 'group-hover:translate-x-0.5')} />
      )}
    </button>
  );
});

const cardBase =
  'group relative w-full overflow-hidden rounded-2xl border text-left outline-none transition-[border-color,background-color,box-shadow] duration-300 focus-visible:ring-2 focus-visible:ring-[#1173F0]/50';
const cardIdle =
  'border-border bg-card/80 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:border-[#1173F0]/30 hover:shadow-[0_18px_44px_-28px_rgba(17,115,240,0.55)]';
const cardSelected =
  'border-[#1173F0]/70 bg-[#1173F0]/[0.035] shadow-[0_0_0_4px_rgba(17,115,240,0.10),0_18px_44px_-28px_rgba(17,115,240,0.6)] dark:border-[#3B8BFF]/70 dark:bg-[#3B8BFF]/[0.06]';

function IconTile({ icon: Icon, selected, size = 'md' }: { icon: LucideIcon; selected: boolean; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cn(
        'relative flex shrink-0 items-center justify-center border transition-[background-color,border-color,color,box-shadow] duration-300',
        size === 'lg' ? 'h-11 w-11 rounded-xl' : 'h-10 w-10 rounded-[11px]',
        selected
          ? 'border-[#1173F0] bg-[#1173F0] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_6px_16px_-6px_rgba(17,115,240,0.7)]'
          : 'border-border/80 bg-gradient-to-b from-background to-muted/60 text-muted-foreground group-hover:text-foreground'
      )}
    >
      <Icon className={size === 'lg' ? 'h-[21px] w-[21px]' : 'h-[19px] w-[19px]'} strokeWidth={1.75} />
    </span>
  );
}

/** Compact selectable row (surfaces). */
export function SelectCard({
  selected,
  title,
  description,
  icon,
  onClick,
  hint,
  badge,
}: {
  selected: boolean;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  hint?: string;
  badge?: string;
}) {
  return (
    <motion.button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      whileTap={{ scale: 0.99 }}
      className={cn(cardBase, 'flex items-start gap-3.5 p-3.5 sm:gap-4 sm:p-4', selected ? cardSelected : cardIdle)}
    >
      <IconTile icon={icon} selected={selected} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {badge && <BluePill>{badge}</BluePill>}
          {hint && (
            <span title={hint} className="inline-flex text-muted-foreground">
              <CircleHelp className="h-3.5 w-3.5" />
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[13px] leading-5 text-muted-foreground sm:text-sm sm:leading-relaxed">
          {description}
        </span>
      </span>
      <Checkbox selected={selected} className="mt-2.5" />
    </motion.button>
  );
}

/** Tall selectable card (intent, payments). */
export function WideSelectCard({
  selected,
  title,
  description,
  icon,
  onClick,
  badge,
  children,
}: {
  selected: boolean;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  badge?: string;
  children?: ReactNode;
}) {
  return (
    <motion.button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      whileTap={{ scale: 0.99 }}
      className={cn(cardBase, 'flex flex-col gap-4 p-5 sm:p-6', selected ? cardSelected : cardIdle)}
    >
      {/* Selected cards pick up a faint brand wash from the top corner. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[radial-gradient(closest-side,rgba(17,115,240,0.16),transparent)] transition-opacity duration-500',
          selected ? 'opacity-100' : 'opacity-0'
        )}
      />
      <span className="relative flex items-start justify-between">
        <IconTile icon={icon} selected={selected} size="lg" />
        <span className="flex items-center gap-2">
          {badge && <BluePill>{badge}</BluePill>}
          <Checkbox selected={selected} />
        </span>
      </span>
      <span className="relative">
        <span className="block text-base font-semibold tracking-[-0.01em] sm:text-[17px]">{title}</span>
        <span className="mt-1.5 block text-[13px] leading-5 text-muted-foreground sm:text-sm sm:leading-[22px]">
          {description}
        </span>
      </span>
      {children && <span className="relative">{children}</span>}
    </motion.button>
  );
}

export function BluePill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border border-[#1173F0]/20 bg-[#1173F0]/[0.06] px-2 py-0.5 text-[11px] font-medium text-[#1173F0] dark:border-[#3B8BFF]/25 dark:bg-[#3B8BFF]/10 dark:text-[#6AA8FF]',
        className
      )}
    >
      {children}
    </span>
  );
}

function Checkbox({ selected, className }: { selected: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-[background-color,border-color] duration-200',
        selected ? 'border-[#1173F0] bg-[#1173F0] text-white' : 'border-muted-foreground/30 bg-background group-hover:border-[#1173F0]/50',
        className
      )}
    >
      <AnimatePresence initial={false}>
        {selected && (
          <motion.span
            className="flex"
            initial={{ scale: 0.3, opacity: 0, rotate: -20 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.3, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 600, damping: 28 }}
          >
            <Check className="h-3 w-3" strokeWidth={3.25} />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

/** Panel used by form-like steps. */
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-card p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-20px_rgba(16,24,40,0.18)] sm:p-6',
        className
      )}
    >
      {children}
    </div>
  );
}

/** Inputs in onboarding are a size up from the app's default, with a brand focus ring. */
export const onboardingInputClass =
  'h-11 rounded-xl border-border bg-background px-3.5 text-[15px] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] focus-visible:border-[#1173F0]/60 focus-visible:ring-4 focus-visible:ring-[#1173F0]/10 focus-visible:ring-offset-0';
